/**
 * Payment Account API client — Host payment collection (Stripe Connect Express).
 *
 * Mirrors the affiliate connect pattern: thin wrappers around fetch().
 * V1: Stripe only. The server abstracts providers; the client is also
 * provider-agnostic so future PayPay/Komoju additions need no client refactor.
 */

const API_BASE = process.env.REACT_APP_API_URL || '';

export type PaymentAccountOwnerType = 'user' | 'organization';
export type PaymentAccountStatus = 'pending' | 'verified' | 'restricted' | 'disabled';
export type PaymentAccountType = 'express' | 'standard';

export interface PaymentAccount {
  id: string;
  ownerType: PaymentAccountOwnerType;
  ownerId: number;
  provider: string;
  /** How it was connected: 'express' (we provisioned) or 'standard' (host's own). */
  accountType: PaymentAccountType;
  externalAccountId: string | null;
  country: string | null;
  defaultCurrency: string | null;
  status: PaymentAccountStatus;
  capabilities: {
    canCharge: boolean;
    canRefund: boolean;
    canPayout: boolean;
  };
  /**
   * Provider-specific extras. `businessName` / `accountEmail` are cached from
   * the provider on each status sync so the host can confirm WHICH account is
   * collecting. Both are best-effort — a provider may report neither.
   */
  metadata?: {
    businessName?: string;
    accountEmail?: string;
    [key: string]: unknown;
  };
  createdAt: string;
  updatedAt: string;
}

export interface MyAccountsResult {
  user: PaymentAccount | null;
  organization: PaymentAccount | null;
  organizationName: string | null;
  organizationId: number | null;
  isOrgAdmin: boolean;
  /** Org policy: may members connect their own personal Stripe account? */
  allowPersonalAccounts: boolean;
  /** Org bank-transfer details used by "Pay After Meeting — bank & others". */
  bankTransferDetails: BankTransferDetails;
}

/**
 * Structured bank-transfer details. Required: bankName, branchName,
 * accountNumber, accountHolder. Optional: accountType, swiftCode, notes.
 */
export interface BankTransferDetails {
  bankName?: string;
  branchName?: string;
  accountType?: string;
  accountNumber?: string;
  accountHolder?: string;
  swiftCode?: string;
  notes?: string;
}

async function authFetch(token: string, path: string, init: RequestInit = {}): Promise<Response> {
  return fetch(`${API_BASE}/api/payment-accounts${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init.headers || {}),
    },
  });
}

export const paymentService = {
  async getMyAccounts(token: string): Promise<MyAccountsResult> {
    const res = await authFetch(token, '/mine');
    if (!res.ok) throw new Error('fetch_failed');
    return res.json();
  },

  /** Express "create new account" — returns the Stripe-hosted onboarding URL. */
  async createOnboardingLink(
    token: string,
    ownerType: PaymentAccountOwnerType
  ): Promise<{ url: string }> {
    const res = await authFetch(token, '/onboarding-link', {
      method: 'POST',
      body: JSON.stringify({ ownerType }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'onboarding_failed');
    }
    return res.json();
  },

  /** Standard "connect existing account" — returns the Stripe OAuth authorize URL. */
  async startStandardConnect(
    token: string,
    ownerType: PaymentAccountOwnerType
  ): Promise<{ url: string }> {
    const res = await authFetch(token, `/stripe/oauth/start?ownerType=${ownerType}`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'oauth_start_failed');
    }
    return res.json();
  },

  /** Org admin: allow/deny members connecting personal Stripe accounts. */
  async setOrgPolicy(token: string, allowPersonalAccounts: boolean): Promise<void> {
    const res = await authFetch(token, '/org-policy', {
      method: 'PATCH',
      body: JSON.stringify({ allowPersonalAccounts }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'update_failed');
    }
  },

  /** Org admin: set the structured bank-transfer details for post-meeting billing. */
  async setOrgBankDetails(token: string, bankTransferDetails: BankTransferDetails): Promise<void> {
    const res = await authFetch(token, '/org-bank-details', {
      method: 'PATCH',
      body: JSON.stringify({ bankTransferDetails }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'update_failed');
    }
  },

  async syncStatus(token: string, accountId: string): Promise<PaymentAccount> {
    const res = await authFetch(token, `/${accountId}/sync-status`, { method: 'POST' });
    if (!res.ok) throw new Error('sync_failed');
    return res.json();
  },

  async openDashboard(token: string, accountId: string): Promise<{ url: string }> {
    const res = await authFetch(token, `/${accountId}/dashboard-link`, { method: 'POST' });
    if (!res.ok) throw new Error('dashboard_link_failed');
    return res.json();
  },

  async disconnect(token: string, accountId: string): Promise<void> {
    const res = await authFetch(token, `/${accountId}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('disconnect_failed');
  },

  /**
   * Phase 3 — Pay Now confirmation. Called by PaymentScreen immediately
   * after `stripe.confirmPayment()` succeeds. Server re-checks with Stripe
   * (we never trust the client), then fires the deferred post-booking actions.
   *
   * Public — no auth header (the booking id IS the handle and Stripe is the
   * ultimate guard).
   *
   * Returns the response shape from POST /api/bookings/:id/confirm-payment.
   *   status: 'paid'    → booking confirmed, side-effects firing
   *   status: 'failed'  → payment declined / failed (caller shows retry UI)
   *   status: 'pending' → still processing, caller polls / waits
   */
  async confirmBookingPayment(bookingId: number): Promise<{
    status: 'paid' | 'failed' | 'pending';
    booking?: any;
    message?: string;
  }> {
    const res = await fetch(`${API_BASE}/api/bookings/${bookingId}/confirm-payment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    const body = await res.json().catch(() => ({}));
    if (res.status === 200) return { status: 'paid', ...body };
    if (res.status === 402) return { status: 'failed', message: body.message };
    if (res.status === 202) return { status: 'pending', message: body.message };
    throw new Error(body.message || 'confirm_failed');
  },
};

export default paymentService;
